import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';
import InteractiveMap from '../components/InteractiveMap';

const Pricing = () => {
  const commonFeatures = [
    'Sınırsız Personel',
    'Sınırsız Randevu',
    'İşletme Yönetim Sistemi ve İçeriklerine Sınırsız Erişim',
    'Web Sitesi Kurulumu',
    'Web Sitesi Tasarımı',
    'Ücretsiz Push Bildirimleri',
    'Detaylı Finans ve Kasa Modülü',
    'Stok ve Satış Yönetimi'
  ];

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const springUp = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    show: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 }
    }
  };

  return (
    <>
      <Helmet>
        <title>Fiyatlandırma | RandevUPP 2.0</title>
        <meta name="description" content="RandevUPP 2.0 fiyatlandırma planları. Sınırsız özellikler, net ve şeffaf fiyatlarla işletmenizi hemen yönetmeye başlayın." />
        <meta property="og:title" content="Fiyatlandırma | RandevUPP 2.0" />
        <meta property="og:description" content="Sınırsız randevu, sınırsız personel ve şeffaf fiyatlarla RandevUPP 2.0 paketleri." />
      </Helmet>

      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="py-16 md:py-24"
      >
        <motion.div variants={springUp} className="text-center mb-16 px-4">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 mb-6">
            Şeffaf Fiyatlar, Sınırsız Özellikler
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistem tek bir tam paket olarak sunulmaktadır. Paket içeriklerinde veya özelliklerde herhangi bir kısıtlama yoktur. Sadece kullanım sürenizi seçin.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-center px-4 mb-24">
          
          {/* Card 1: 6 Aylık */}
          <motion.div 
            variants={springUp}
            whileHover={{ y: -10 }}
            className="glass-panel p-8 rounded-[2.5rem] flex flex-col h-full transition-all duration-300"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">6 Aylık</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 font-medium">Kısa vadeli çözüm arayanlar için ideal başlangıç.</p>
            
            <div className="mb-8">
              <span className="text-gray-400 line-through text-lg font-medium mr-2">16.000₺</span>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-extrabold text-gray-900 dark:text-white tracking-tight">14.000₺</span>
                <span className="text-sm font-semibold text-gray-500 mb-2">+ KDV</span>
              </div>
            </div>

            <Link to="/contact" className="w-full btn-secondary text-center mb-8 hover:bg-gray-100 dark:hover:bg-gray-800">
              Hemen Başla
            </Link>

            <div className="space-y-5 mt-auto">
              <p className="font-bold text-sm text-gray-900 dark:text-white mb-2 uppercase tracking-wider">Pakete Dahil Olanlar</p>
              {commonFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-primary-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Card 2: 12 Aylık (Popular) */}
          <motion.div 
            variants={springUp}
            whileHover={{ y: -10 }}
            className="bg-gradient-to-b from-primary-600 to-primary-800 text-white p-8 md:p-10 rounded-[3rem] flex flex-col h-full relative transform lg:scale-105 shadow-2xl ring-4 ring-primary-500/30 transition-all duration-300"
          >
            <div className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-1/2 px-6 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-sm rounded-full shadow-lg whitespace-nowrap animate-pulse">
              Öne Çıkan / Kampanya
            </div>
            
            <h3 className="text-3xl font-bold mb-2">12 Aylık</h3>
            <p className="text-primary-100 text-sm mb-6 font-medium">RandevUPP 2.0 Güncellemesine Özel Kampanya!</p>
            
            <div className="mb-8">
              <span className="text-primary-300 line-through text-lg font-medium mr-2">26.000₺</span>
              <div className="flex items-end gap-1">
                <span className="text-6xl font-extrabold text-white tracking-tight">24.000₺</span>
                <span className="text-sm font-semibold text-primary-200 mb-2">+ KDV</span>
              </div>
            </div>

            <Link to="/contact" className="w-full px-6 py-4 bg-white text-primary-600 hover:bg-gray-100 font-bold rounded-full transition-all text-center shadow-xl active:scale-95 mb-8 text-lg">
              Avantajı Yakala
            </Link>

            <div className="space-y-5 mt-auto">
              <p className="font-bold text-sm text-white mb-2 uppercase tracking-wider">Pakete Dahil Olanlar</p>
              {commonFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-primary-300 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-primary-50">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Card 3: Sınırsız */}
          <motion.div 
            variants={springUp}
            whileHover={{ y: -10 }}
            className="glass-panel p-8 rounded-[2.5rem] flex flex-col h-full transition-all duration-300"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Sınırsız (Ömür Boyu)</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 font-medium">Bir kere ödeyin, sistemi ömür boyu sınırsız kullanın.</p>
            
            <div className="mb-8 py-2">
              <div className="flex items-end gap-1">
                <span className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400">Bize Ulaşın</span>
              </div>
            </div>

            <Link to="/contact" className="w-full btn-secondary text-center mb-8 flex items-center justify-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800">
              <PhoneCall size={18} /> Detayları Konuşalım
            </Link>

            <div className="space-y-5 mt-auto">
              <p className="font-bold text-sm text-gray-900 dark:text-white mb-2 uppercase tracking-wider">Pakete Dahil Olanlar</p>
              {commonFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-primary-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{feature}</span>
                </div>
              ))}
              <div className="flex items-start gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                <CheckCircle2 size={20} className="text-blue-500 shrink-0 mt-0.5" />
                <span className="text-sm font-bold text-gray-900 dark:text-white">Ömür Boyu Güncelleme Desteği</span>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Map CTA Section */}
        <motion.div 
          variants={springUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="border-t border-gray-200 dark:border-gray-800 pt-16"
        >
          <div className="text-center mb-8 px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Şehrinizdeki İlk Müşterimiz Olun!</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
              Haritada henüz aktif olmadığımız bir şehirdeyseniz, <strong className="text-primary-600 dark:text-primary-400">ilk müşterimize özel %20 ekstra indirim</strong> fırsatı sizi bekliyor.
            </p>
          </div>
          <InteractiveMap />
        </motion.div>

      </motion.div>
    </>
  );
};

export default Pricing;
